/**
 * Camada única de agregação — Central de Problemas.
 */
import type { Issue } from '../types/issue';
import {
  getDaysSinceCreated,
  getSlaRemainingDays,
  isSlaOverdue,
  isSlaWarning,
} from '../utils/issueSla';
import { getIssueHistory } from '../utils/issueHistory';

export type ProblemQuickFilter = 'open' | 'critical' | 'sla_overdue' | 'unassigned' | 'recurring';

export interface ProblemIssueFlags {
  critical: boolean;
  slaOverdue: boolean;
  slaWarning: boolean;
  unassigned: boolean;
  recurring: boolean;
}

export interface EnrichedProblemIssue extends Issue {
  flags: ProblemIssueFlags;
  slaRemainingDays: number;
  daysOpen: number;
  priorityScore: number;
}

export interface ProblemExecutiveSummary {
  total: number;
  open: number;
  critical: number;
  slaOverdue: number;
  unassigned: number;
  recurring: number;
  structured: number;
  avgDaysOpen: number;
}

export interface ProblemDashboardData {
  summary: ProblemExecutiveSummary;
  issues: EnrichedProblemIssue[];
  priorityQueue: EnrichedProblemIssue[];
}

const OPEN_STATUSES = new Set(['open', 'investigation', 'root_cause', 'action_plan', 'execution', 'validation', 'monitoring']);

function isRecurringIssue(issue: Issue): boolean {
  if (issue.metadata?.recurrence_detected) return true;
  return (issue.tags ?? []).some(t => /recorr/i.test(t));
}

function computePriorityScore(issue: EnrichedProblemIssue): number {
  let score = 0;
  if (issue.flags.critical) score += 100;
  if (issue.flags.slaOverdue) score += 80;
  if (issue.flags.unassigned) score += 40;
  if (issue.flags.recurring) score += 30;
  if (issue.flags.slaWarning) score += 20;
  if (OPEN_STATUSES.has(issue.status)) score += 10;
  score += Math.min(issue.daysOpen, 30);
  return score;
}

export function enrichIssue(issue: Issue): EnrichedProblemIssue {
  const flags: ProblemIssueFlags = {
    critical: issue.severity === 'critical' && issue.status !== 'closed',
    slaOverdue: isSlaOverdue(issue),
    slaWarning: isSlaWarning(issue),
    unassigned: !issue.assigned_to && issue.status !== 'closed',
    recurring: isRecurringIssue(issue),
  };
  const daysOpen = getDaysSinceCreated(issue.created_at);
  const slaRemainingDays = getSlaRemainingDays(issue.severity, issue.created_at, issue.status);
  const enriched: EnrichedProblemIssue = {
    ...issue,
    flags,
    daysOpen,
    slaRemainingDays: Number.isFinite(slaRemainingDays) ? slaRemainingDays : 999,
    priorityScore: 0,
  };
  enriched.priorityScore = computePriorityScore(enriched);
  return enriched;
}

export function buildProblemDashboard(issues: Issue[]): ProblemDashboardData {
  const enriched = issues.map(enrichIssue);
  const openIssues = enriched.filter(i => OPEN_STATUSES.has(i.status));

  const summary: ProblemExecutiveSummary = {
    total: enriched.length,
    open: openIssues.length,
    critical: openIssues.filter(i => i.flags.critical).length,
    slaOverdue: openIssues.filter(i => i.flags.slaOverdue).length,
    unassigned: openIssues.filter(i => i.flags.unassigned).length,
    recurring: openIssues.filter(i => i.flags.recurring).length,
    structured: openIssues.filter(i => i.mode === 'structured_problem' && i.status !== 'closed').length,
    avgDaysOpen: openIssues.length
      ? Math.round(openIssues.reduce((s, i) => s + i.daysOpen, 0) / openIssues.length)
      : 0,
  };

  const priorityQueue = [...openIssues]
    .sort((a, b) => b.priorityScore - a.priorityScore)
    .slice(0, 10);

  return { summary, issues: enriched, priorityQueue };
}

export function filterByQuickFilter(
  issues: EnrichedProblemIssue[],
  filter: ProblemQuickFilter | null,
): EnrichedProblemIssue[] {
  if (!filter) return issues;
  switch (filter) {
    case 'open':
      return issues.filter(i => OPEN_STATUSES.has(i.status));
    case 'critical':
      return issues.filter(i => i.flags.critical);
    case 'sla_overdue':
      return issues.filter(i => i.flags.slaOverdue);
    case 'unassigned':
      return issues.filter(i => i.flags.unassigned);
    case 'recurring':
      return issues.filter(i => i.flags.recurring);
    default:
      return issues;
  }
}

export function formatHistoryTime(iso: string): string {
  const d = new Date(iso);
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  if (diffDays === 0) return d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  if (diffDays === 1) return 'Ontem';
  if (diffDays < 7) return `${diffDays}d atrás`;
  return d.toLocaleDateString('pt-BR');
}

export { getIssueHistory };
